import { Link } from "@inertiajs/react"
import { ArrowRight, Store } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { SectionHeader } from "@/components/ui/headers"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures } from "@/features/staff/reports/report-block"
import { formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * Branches side by side, with the spread already computed.
 *
 * **The index is the product.** A consolidated total is what every competitor
 * ships and it is not what an owner of three restaurants looks at — they are
 * looking for the branch that is half the others. So each row carries its
 * position against the group AVERAGE as a ratio, and the bar underneath is that
 * ratio made visible. 1.00 is exactly average; the reference line sits there.
 *
 * A branch that did not trade renders the absence rather than a zero index. Zero
 * would read as "sold nothing relative to its peers", which is a claim about
 * performance; "cerrada" is a claim about opening, and they are different
 * conversations.
 */

export type BranchRow = {
  locationId: number
  name: string
  timeZone: string
  shiftCount: number
  transactionCount: number
  subtotal: string
  averageTicket: string
  tips: string
  share: number
  index: number | null
  traded: boolean
}

export type Comparison = {
  branches: BranchRow[]
  branchCount: number
  transactionCount: number
  subtotal: string
  averageSubtotal: string | null
  comparable: boolean
  label: string | null
}

function tOrg(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.organization.show.${key}`, options)
}

/** Where the bar sits, with 1.00 (the group average) at the midpoint. */
function barWidth(index: number | null): number {
  if (index === null) return 0

  return Math.max(2, Math.min(100, Math.round((index / 2) * 100)))
}

function BranchRowItem({ row, isCurrent }: { row: BranchRow; isCurrent: boolean }) {
  return (
    <li className="space-y-1.5 px-4 py-3" data-testid={`branch-row-${row.locationId}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium">{row.name}</span>
          {isCurrent ? (
            <span
              className="text-muted-foreground shrink-0 text-xs"
              data-testid={`branch-current-${row.locationId}`}
            >
              {tOrg("current_branch")}
            </span>
          ) : null}
        </span>
        <span className="text-foreground shrink-0 text-sm font-semibold tabular-nums">
          {formatMoney(row.subtotal)}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground text-xs tabular-nums">
          {tOrg("row_meta", { transactions: row.transactionCount, shifts: row.shiftCount })}
        </span>
        {row.traded && row.index !== null ? (
          <span
            className="text-muted-foreground text-xs tabular-nums"
            data-testid={`branch-index-${row.locationId}`}
          >
            {tOrg("index_value", { index: row.index.toFixed(2) })}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs" data-testid={`branch-closed-${row.locationId}`}>
            {tOrg("did_not_trade")}
          </span>
        )}
      </div>

      {/* The dot rule: the comparison lives in a 2px bar, never in coloured
          type. The reference mark at the midpoint is what makes the bar mean
          "against the average" rather than "against the biggest". */}
      <div className="bg-muted relative h-2 overflow-hidden rounded-full">
        <div
          className="bg-foreground/70 h-full rounded-full"
          style={{ width: `${barWidth(row.index)}%` }}
        />
        <span className="bg-border absolute inset-y-0 left-1/2 w-px" aria-hidden="true" />
      </div>
    </li>
  )
}

export function BranchComparison({
  comparison,
  currentLocationId,
}: {
  comparison: Comparison
  currentLocationId: number
}) {
  return (
    <Card density="compact" data-testid="branch-comparison">
      <CardHeader className="border-b">
        <CardTitle>{tOrg("title")}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <ReportFigures>
          <TillFigure
            label={tOrg("branches_label")}
            value={String(comparison.branchCount)}
            testId="organization-branches"
          />
          <TillFigure
            label={tOrg("consolidated_label")}
            value={formatMoney(comparison.subtotal)}
            testId="organization-consolidated"
          />
          {comparison.averageSubtotal ? (
            <TillFigure
              label={tOrg("average_label")}
              value={formatMoney(comparison.averageSubtotal)}
              hint={tOrg("average_hint")}
              testId="organization-average"
            />
          ) : null}
        </ReportFigures>

        <div className="space-y-2">
          <SectionHeader title={tOrg("branches_title")} className="px-0" />

          {comparison.comparable ? (
            <ul className="divide-border divide-y rounded-md border" data-testid="branch-list">
              {comparison.branches.map((row) => (
                <BranchRowItem
                  key={row.locationId}
                  row={row}
                  isCurrent={row.locationId === currentLocationId}
                />
              ))}
            </ul>
          ) : (
            /* One branch, or a group that sold nothing. Either way the spread is
               not a thing yet, and inventing a table of one row would dress an
               ordinary report up as a comparison. */
            <p className="text-muted-foreground text-sm" data-testid="branch-not-comparable">
              {tOrg("not_comparable")}
            </p>
          )}
        </div>

        <Link
          href="/reports"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          data-testid="organization-to-reports"
        >
          <Store className="size-3" aria-hidden="true" />
          {tOrg("to_branch_report")}
          <ArrowRight className="size-3" aria-hidden="true" />
        </Link>
      </CardContent>
    </Card>
  )
}
